import { z } from 'zod';

import { makeEndpoint } from '../common';

// ── Enums ──────────────────────────────────────────────────────────────

export enum ResourcePoolStatus {
  ACTIVE = 'active',
  /** Temporarily offline (seasonal closure, maintenance) — kept visible, sells nothing. */
  INACTIVE = 'inactive',
  ARCHIVED = 'archived',
}

export enum ResourcePoolType {
  EQUIPMENT = 'equipment',
  SPACE = 'space',
  STAFF = 'staff',
  VEHICLE = 'vehicle',
  OTHER = 'other',
}

/** Unit lifecycle on the wire; overrides blackouts. */
export const resourcePoolUnitStatusSchema = z.enum(['active', 'inactive', 'archived']);
export type ResourcePoolUnitStatus = z.infer<typeof resourcePoolUnitStatusSchema>;

export enum ResourcePoolUnitTrackingMode {
  BULK = 'BULK',
  TRACKED = 'TRACKED',
}

/** Storage spelling; the wire spells these camelCase (see ResourceAllocationStatusWireSchema). */
export enum ResourceAllocationStatus {
  CONFIRMED = 'CONFIRMED',
  PICKED_UP = 'PICKED_UP',
  RETURNED = 'RETURNED',
  NO_SHOW = 'NO_SHOW',
  CANCELED = 'CANCELED',
  /** Holds the unit while a card payment is pending, until expiresAt. */
  HELD = 'HELD',
}

/** How a pool came to exist: the resources tab, the collection or experience builder, or the resource group backfill. */
export enum ResourcePoolOrigin {
  MANUAL = 'manual',
  RGC_BACKFILL = 'rgc_backfill',
  COLLECTION_BUILDER = 'collection_builder',
  EXPERIENCE_BUILDER = 'experience_builder',
}

/**
 * Who an allocation belongs to. BOOKING rows carry a bookingId; SESSION rows
 * carry an eventId (session-scoped staffing: the guide belongs to the 2 PM
 * departure, not to the booking that pushed attendance over the ratio).
 */
export enum ResourceAllocationSourceType {
  BOOKING = 'BOOKING',
  SESSION = 'SESSION',
}

/**
 * What `per` counts when a requirement is read as "allocate `quantity` units
 * for every `per` `scope`": the session itself, bookings in the session, or
 * participants in the session.
 */
export enum ResourceRequirementScope {
  BOOKING = 'booking',
  SESSION = 'session',
  PARTICIPANT = 'participant',
}

export const ResourceAllocationStatusWireSchema = z.enum([
  'confirmed',
  'pickedUp',
  'returned',
  'noShow',
  'canceled',
  'held',
]);
/** Literal union rather than the enum so callers can pass 'TRACKED' directly; ResourcePoolUnitTrackingMode carries the same values. */
const unitTrackingModeSchema = z.enum(['BULK', 'TRACKED']);
export type ResourceAllocationStatusWire = z.infer<typeof ResourceAllocationStatusWireSchema>;

const dateString = () => z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// ── Schemas ────────────────────────────────────────────────────────────

// ── Attributes ─────────────────────────────────────────────────────────

export const ResourceAttributeValueSchema = z.object({
  resourceAttributeValueId: z.string().uuid(),
  label: z.string(),
  sortOrder: z.number().int(),
});
export type ResourceAttributeValue = z.infer<typeof ResourceAttributeValueSchema>;

/** Brand-scoped dimension (Size, Category, Language) that pools opt into. */
export const ResourceAttributeSchema = z.object({
  resourceAttributeId: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
  values: z.array(ResourceAttributeValueSchema),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type ResourceAttribute = z.infer<typeof ResourceAttributeSchema>;

/** An attribute as one pool uses it. */
export const ResourcePoolAttributeSchema = z.object({
  resourceAttributeId: z.string().uuid(),
  name: z.string(),
  /** One unit may carry several values (a therapist speaking two languages). */
  allowMultiple: z.boolean(),
  /** Experience rules may let the guest pick a value at checkout. */
  guestSelectable: z.boolean(),
  sortOrder: z.number().int(),
  values: z.array(ResourceAttributeValueSchema),
});
export type ResourcePoolAttribute = z.infer<typeof ResourcePoolAttributeSchema>;

const attributeName = () => z.string().trim().min(1).max(100);
const attributeLabel = () => z.string().trim().min(1).max(100);

export const CreateResourceAttributeRequestSchema = z
  .object({
    brandId: z.string().uuid(),
    name: attributeName(),
    values: z
      .array(z.object({ label: attributeLabel() }).strict())
      .min(1)
      .max(100),
  })
  .strict();
export type CreateResourceAttributeRequest = z.infer<typeof CreateResourceAttributeRequestSchema>;

/** `values` replaces the list: items with an id are kept (and relabelled), items without are created, missing ones removed. */
export const UpdateResourceAttributeRequestSchema = z
  .object({
    name: attributeName().optional(),
    values: z
      .array(z.object({ resourceAttributeValueId: z.string().uuid().optional(), label: attributeLabel() }).strict())
      .min(1)
      .max(100)
      .optional(),
  })
  .strict()
  .refine((data) => Object.keys(data).length > 0, 'Provide at least one field to update');
export type UpdateResourceAttributeRequest = z.infer<typeof UpdateResourceAttributeRequestSchema>;

/** Replaces the pool's attribute set; order in the array becomes sortOrder. */
export const ResourcePoolAttributeInputSchema = z
  .object({
    resourceAttributeId: z.string().uuid(),
    allowMultiple: z.boolean().optional(),
    guestSelectable: z.boolean().optional(),
  })
  .strict();
export type ResourcePoolAttributeInput = z.infer<typeof ResourcePoolAttributeInputSchema>;

/** A pinned value narrows eligible units; a null value lets the guest pick at checkout. */
export const ResourceRequirementAttributeSchema = z
  .object({
    resourceAttributeId: z.string().uuid(),
    resourceAttributeValueId: z.string().uuid().nullable(),
    /**
     * How several pinned values of one attribute combine: `all` (a unit needs every one) or `any`
     * (at least one). Every row of the attribute carries the same value; omitted means `all`.
     */
    match: z.enum(['all', 'any']).optional(),
  })
  .strict();
export type ResourceRequirementAttribute = z.infer<typeof ResourceRequirementAttributeSchema>;

const poolAttributesInput = () => z.array(ResourcePoolAttributeInputSchema).max(20);
const unitAttributeValueIds = () => z.array(z.string().uuid()).max(50);

/** Persisted canonical resources. Dashboard projections below retain their UI IDs. */
export const ResourcePoolSchema = z.object({
  resourcePoolId: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
  type: z.nativeEnum(ResourcePoolType),
  status: z.nativeEnum(ResourcePoolStatus),
  unitQuantity: z.number().int().nonnegative(),
  unitTrackingMode: unitTrackingModeSchema,
  capacityPerUnit: z.number().int().positive().nullable(),
  sortOrder: z.number().int(),
  attributes: z.array(ResourcePoolAttributeSchema),
  origin: z.nativeEnum(ResourcePoolOrigin).optional(),
  /** Hidden pools still allocate; they are left out of the pools tab and pickers. */
  isVisible: z.boolean().optional(),
  /** Distinct resource group collections whose groups sell this pool's units. */
  usedByCount: z.number().int().nonnegative().optional(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type ResourcePool = z.infer<typeof ResourcePoolSchema>;

export const ResourcePoolUnitSchema = z.object({
  resourcePoolUnitId: z.string().uuid(),
  resourcePoolId: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
  status: resourcePoolUnitStatusSchema,
  notes: z.string().nullable(),
  attributeValueIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type ResourcePoolUnit = z.infer<typeof ResourcePoolUnitSchema>;

export const ResourcePoolUnitBlackoutSchema = z.object({
  resourcePoolUnitId: z.string().uuid(),
  startDate: dateString(),
  endDate: dateString().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type ResourcePoolUnitBlackout = z.infer<typeof ResourcePoolUnitBlackoutSchema>;

export const ResourceRequirementResourceSchema = z.object({
  resourceRequirementId: z.string().uuid(),
  experienceId: z.string().uuid(),
  resourcePoolId: z.string().uuid(),
  quantity: z.number().int().positive(),
  per: z.number().int().positive(),
  scope: z.nativeEnum(ResourceRequirementScope),
  allowPartySplit: z.boolean().nullable(),
  priceTierNames: z.array(z.string()).nullable(),
  attributes: z.array(ResourceRequirementAttributeSchema),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type ResourceRequirementResource = z.infer<typeof ResourceRequirementResourceSchema>;

export const ResourceAllocationSchema = z.object({
  resourceAllocationId: z.string().uuid(),
  brandId: z.string().uuid(),
  resourcePoolId: z.string().uuid(),
  resourcePoolUnitId: z.string().uuid().nullable(),
  bookingId: z.string().uuid().nullable(),
  eventId: z.string().uuid().nullable(),
  sourceType: z.nativeEnum(ResourceAllocationSourceType),
  status: ResourceAllocationStatusWireSchema,
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
  quantity: z.number().int().positive(),
  /** Set for participant-scoped rows: the participant this unit serves. */
  bookingParticipantId: z.string().uuid().nullable(),
  /** The guest chose this exact unit; staff moves are refused. */
  requested: z.boolean(),
  /** HELD rows only: when the pending payment's hold lapses. */
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
  /** What the booking asked for (pins plus guest picks): the unit carries every one. */
  attributeValueIds: z.array(z.string().uuid()),
  /** Pinned "any of" groups: the unit carries at least one value of each group. */
  anyOfValueIds: z.array(z.array(z.string().uuid())),
  pickedUpAt: z.string().datetime({ offset: true }).nullable(),
  returnedAt: z.string().datetime({ offset: true }).nullable(),
  noShowAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type ResourceAllocation = z.infer<typeof ResourceAllocationSchema>;

/** Repeatable: `?brandId=a&brandId=b`. The brand access guard unions the values into the caller's scope. */
const brandFilter = z.union([z.string().uuid(), z.array(z.string().uuid()).min(1).max(50)]).optional();

export const ResourceListQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    after: z.string().min(1).optional(),
    q: z.string().max(255).optional(),
  })
  .strict();
export const ListResourcePoolsQuerySchema = ResourceListQuerySchema.extend({
  brandId: brandFilter,
  /** Include pools hidden from the pools tab (backfilled, not classified as rentals). */
  includeHidden: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => value === true || value === 'true')
    .optional(),
});
export const ListResourceAttributesQuerySchema = ResourceListQuerySchema.extend({ brandId: brandFilter });
export const ListResourcePoolUnitsQuerySchema = ResourceListQuerySchema;
export const ListResourceRequirementsQuerySchema = ResourceListQuerySchema;
export const ListResourcePoolUnitBlackoutsQuerySchema = ResourceListQuerySchema.omit({ q: true });
/** Stored statuses plus the calculated atRisk; rows keep their stored status. */
export const ResourceAllocationStatusFilterSchema = z.enum([...ResourceAllocationStatusWireSchema.options, 'atRisk']);
export type ResourceAllocationStatusFilter = z.infer<typeof ResourceAllocationStatusFilterSchema>;

const allocationFilterSchema = z.object({
  brandId: brandFilter,
  experienceId: z.string().uuid().optional(),
  resourcePoolId: z.string().uuid().optional(),
  resourcePoolUnitId: z.string().uuid().optional(),
  bookingId: z.string().uuid().optional(),
  eventId: z.string().uuid().optional(),
  status: z.union([ResourceAllocationStatusFilterSchema, z.array(ResourceAllocationStatusFilterSchema).max(6)]).optional(),
  /** Treat this unit as offline when computing atRisk; presence enables the simulation. */
  simulateDisabledResourcePoolUnitId: z.string().uuid().optional(),
  /** Overlap window: allocations with startAt < to and endAt > from. */
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export const ListResourceAllocationsQuerySchema = ResourceListQuerySchema.omit({ q: true }).extend(
  allocationFilterSchema.shape,
);
export type ResourceListQuery = z.infer<typeof ResourceListQuerySchema>;
export type ListResourceAllocationsQuery = z.infer<typeof ListResourceAllocationsQuerySchema>;

const resourcePageSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    hasMore: z.boolean(),
    nextCursor: z.string().optional(),
  });
export const ResourcePoolPageSchema = resourcePageSchema(ResourcePoolSchema);
export const ResourceAttributePageSchema = resourcePageSchema(ResourceAttributeSchema);
export const ResourcePoolUnitPageSchema = resourcePageSchema(ResourcePoolUnitSchema);
export const ResourcePoolUnitBlackoutPageSchema = resourcePageSchema(ResourcePoolUnitBlackoutSchema);
export const ResourceRequirementPageSchema = resourcePageSchema(ResourceRequirementResourceSchema);
export const ResourceAllocationPageSchema = resourcePageSchema(ResourceAllocationSchema);

export const ResourcePoolResponseSchema = z.object({
  id: z.string().uuid(),
  brandId: z.string().uuid(),
  name: z.string(),
  type: z.nativeEnum(ResourcePoolType),
  status: z.nativeEnum(ResourcePoolStatus),
  unitQuantity: z.number().int().nonnegative(),
  unitTrackingMode: unitTrackingModeSchema,
  /** Active named units. */
  unitCount: z.number().int().nonnegative().optional(),
  origin: z.nativeEnum(ResourcePoolOrigin).optional(),
  isVisible: z.boolean().optional(),
  usedByCount: z.number().int().nonnegative().optional(),
  /** How many people one unit serves at once (e.g. seats in a van). Null when not applicable. */
  capacityPerUnit: z.number().int().positive().nullable().optional(),
  sortOrder: z.number().int(),
  attributes: z.array(ResourcePoolAttributeSchema),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export const ResourcePoolProjectionWireSchema = ResourcePoolResponseSchema.omit({ id: true }).extend({
  resourcePoolId: z.string().uuid(),
});
export type ResourcePoolProjectionWire = z.infer<typeof ResourcePoolProjectionWireSchema>;
export const ResourcePoolProjectionPageSchema = resourcePageSchema(ResourcePoolProjectionWireSchema);

export type ResourcePoolResponse = z.infer<typeof ResourcePoolResponseSchema>;

/** No `unitTrackingMode`: pools are created tracked in this release; the field is read-only until bulk lands. */
export const CreateResourcePoolRequestSchema = z.object({
  brandId: z.string().uuid(),
  name: z.string().min(1).max(255),
  type: z.nativeEnum(ResourcePoolType),
  unitQuantity: z.number().int().nonnegative(),
  /** How many people one unit serves at once (e.g. seats in a van). */
  capacityPerUnit: z.number().int().positive().nullable().optional(),
  /** Tracked pools only: create one named unit per entry in the same transaction. */
  unitNames: z.array(z.string().min(1).max(255)).max(500).optional(),
  attributes: poolAttributesInput().optional(),
  /** Where the pool was created from; the server sets the other sources itself. */
  origin: z.enum([ResourcePoolOrigin.MANUAL, ResourcePoolOrigin.EXPERIENCE_BUILDER]).optional(),
});

export const CreateCanonicalResourcePoolRequestSchema = CreateResourcePoolRequestSchema.omit({ unitNames: true });

export type CreateResourcePoolRequest = z.infer<typeof CreateResourcePoolRequestSchema>;

export const UpdateResourcePoolRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  type: z.nativeEnum(ResourcePoolType).optional(),
  /** Take the whole pool offline (or back). Archiving stays a separate action. */
  status: z.union([z.literal(ResourcePoolStatus.ACTIVE), z.literal(ResourcePoolStatus.INACTIVE)]).optional(),
  unitQuantity: z.number().int().nonnegative().optional(),
  /**
   * Tracked only this release, matching create: the API 422s on `bulk`, so the
   * literal keeps that refusal in the type instead of at runtime. Widening it
   * when bulk lands makes the blast radius compiler-visible.
   */
  unitTrackingMode: z.literal(ResourcePoolUnitTrackingMode.TRACKED).optional(),
  capacityPerUnit: z.number().int().positive().nullable().optional(),
  attributes: poolAttributesInput().optional(),
  /** Show or hide the pool in the pools tab and pickers. */
  isVisible: z.boolean().optional(),
});

export type UpdateResourcePoolRequest = z.infer<typeof UpdateResourcePoolRequestSchema>;

/**
 * The one cross-field rule, shared so both the create and the update path
 * apply it. Undefined `per` passes: a PATCH carrying only one of the pair is
 * merged against the stored row, which the API validates.
 */
export const isPerValidForScope = (data: {
  per?: number | undefined;
  scope?: ResourceRequirementScope | undefined;
  [key: string]: unknown;
}): boolean => data.scope !== ResourceRequirementScope.SESSION || data.per === undefined || data.per === 1;

export const PER_SCOPE_REFINEMENT: { message: string; path: (string | number)[] } = {
  message: 'per must be 1 when scope is session',
  path: ['per'],
};

const requirementFields = {
  resourcePoolId: z.string().uuid(),
  /** Units to allocate for every `per` of the scope. */
  quantity: z.number().int().positive(),
  /** How many of the scope each `quantity` covers; must be 1 when scope is session. */
  per: z.number().int().positive(),
  scope: z.nativeEnum(ResourceRequirementScope),
  /** Whether one booking may be served by more than one unit. */
  allowPartySplit: z.boolean().nullable().optional(),
  /**
   * Price tiers this rule applies to, matched by name like the rest of booking.
   * Null or absent applies to every tier; an empty list would never apply, so it is refused.
   */
  priceTierNames: z.array(z.string().min(1)).min(1).max(50).nullable().optional(),
  attributes: z.array(ResourceRequirementAttributeSchema).max(20).optional(),
};

export const ResourceRequirementObjectSchema = z.object(requirementFields);

export const ResourceRequirementSchema = ResourceRequirementObjectSchema.refine(isPerValidForScope, PER_SCOPE_REFINEMENT);

export type ResourceRequirement = z.infer<typeof ResourceRequirementSchema>;

/** Plain object, so callers can still `.strict()` and `.required()` it. */
export const UpdateResourceRequirementRequestObjectSchema = z.object({
  resourcePoolId: requirementFields.resourcePoolId.optional(),
  quantity: requirementFields.quantity.optional(),
  per: requirementFields.per.optional(),
  scope: requirementFields.scope.optional(),
  allowPartySplit: requirementFields.allowPartySplit,
  priceTierNames: requirementFields.priceTierNames,
  attributes: requirementFields.attributes,
});

/**
 * Hand-rebuilding the fields to make them optional dropped the session/per
 * rule, so a PATCH could set scope to session while leaving per above 1, which
 * no allocation can satisfy.
 */
export const UpdateResourceRequirementRequestSchema = UpdateResourceRequirementRequestObjectSchema.refine(
  isPerValidForScope,
  PER_SCOPE_REFINEMENT,
);
export type UpdateResourceRequirementRequest = z.infer<typeof UpdateResourceRequirementRequestSchema>;

export const ResourceRequirementResponseSchema = z.object({
  id: z.string().uuid(),
  experienceId: z.string().uuid(),
  resourcePoolId: z.string().uuid(),
  resourcePoolName: z.string(),
  resourcePoolType: z.nativeEnum(ResourcePoolType),
  quantity: z.number().int().positive(),
  per: z.number().int().positive(),
  scope: z.nativeEnum(ResourceRequirementScope),
  allowPartySplit: z.boolean().nullable().optional(),
  priceTierNames: z.array(z.string()).nullable(),
  attributes: z.array(ResourceRequirementAttributeSchema),
});

export const ResourceRequirementProjectionWireSchema = ResourceRequirementResponseSchema.omit({ id: true }).extend({
  resourceRequirementId: z.string().uuid(),
});
export type ResourceRequirementProjectionWire = z.infer<typeof ResourceRequirementProjectionWireSchema>;
export const ResourceRequirementProjectionPageSchema = resourcePageSchema(ResourceRequirementProjectionWireSchema);

export type ResourceRequirementResponse = z.infer<typeof ResourceRequirementResponseSchema>;

export const UpsertResourceRequirementsRequestSchema = z.object({
  requirements: z.array(ResourceRequirementSchema),
});

export type UpsertResourceRequirementsRequest = z.infer<typeof UpsertResourceRequirementsRequestSchema>;

// ── Endpoints ──────────────────────────────────────────────────────────

export const ListResourcePoolsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, includeHidden }: { brandId: string; includeHidden?: boolean }) =>
    `v1/dashboard/resources/pools?brandId=${encodeURIComponent(brandId)}${includeHidden ? '&includeHidden=true' : ''}`,
  requestSchema: z.void(),
  responseSchema: ResourcePoolProjectionPageSchema,
});

export const GetResourcePoolEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ resourcePoolId }: { resourcePoolId: string }) => `v1/resource-pools/${resourcePoolId}`,
  requestSchema: z.void(),
  responseSchema: ResourcePoolSchema,
});

export const CreateResourcePoolEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/dashboard/resources/pools`,
  requestSchema: CreateResourcePoolRequestSchema,
  responseSchema: ResourcePoolProjectionWireSchema,
});

export const UpdateResourcePoolsRequestSchema = z.object({
  items: z
    .array(
      UpdateResourcePoolRequestSchema.extend({
        resourcePoolId: z.string().uuid(),
        sortOrder: z.number().int().nonnegative().optional(),
      })
        .strict()
        .refine((item) => Object.keys(item).length > 1, 'Provide at least one field to update'),
    )
    .min(1)
    .max(500),
});
export type UpdateResourcePoolsRequest = z.infer<typeof UpdateResourcePoolsRequestSchema>;
export const UpdateResourcePoolsResponseSchema = z.object({ items: z.array(ResourcePoolSchema) });
export const UpdateResourcePoolsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: () => `v1/resource-pools`,
  requestSchema: UpdateResourcePoolsRequestSchema,
  responseSchema: UpdateResourcePoolsResponseSchema,
});
export const DeleteResourcePoolsRequestSchema = z.object({
  items: z
    .array(z.object({ resourcePoolId: z.string().uuid() }).strict())
    .min(1)
    .max(500),
});
export const DeleteResourcePoolsEndpoint = makeEndpoint({
  method: 'DELETE',
  url: () => `v1/resource-pools`,
  requestSchema: DeleteResourcePoolsRequestSchema,
  responseSchema: z.void(),
});

export const GetExperienceResourceRequirementsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ experienceId }: { brandId?: string; experienceId: string }) =>
    `v1/dashboard/resources/experiences/${experienceId}/requirements`,
  requestSchema: z.void(),
  responseSchema: ResourceRequirementProjectionPageSchema,
});

export const ExperienceHasResourceRequirementsSchema = z.object({ hasRequirements: z.boolean() });
export type ExperienceHasResourceRequirements = z.infer<typeof ExperienceHasResourceRequirementsSchema>;
/** Booking screens only need "does this experience consume pool inventory?"; readable by the promoter too. */
export const GetExperienceHasResourceRequirementsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, experienceId }: { brandId: string; experienceId: string }) =>
    `v1/dashboard/resources/experiences/${experienceId}/requirements/exists?brandId=${encodeURIComponent(brandId)}`,
  requestSchema: z.void(),
  responseSchema: ExperienceHasResourceRequirementsSchema,
});

export const UpsertExperienceResourceRequirementsEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ experienceId }: { brandId?: string; experienceId: string }) =>
    `v1/dashboard/resources/experiences/${experienceId}/requirements`,
  requestSchema: UpsertResourceRequirementsRequestSchema,
  responseSchema: z.void(),
});

// ── Attributes ───────────────────────────────────────────────────

export const ListResourceAttributesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, after }: { brandId: string; after?: string }) =>
    `v1/resource-attributes?brandId=${encodeURIComponent(brandId)}&limit=100${
      after ? `&after=${encodeURIComponent(after)}` : ''
    }`,
  requestSchema: z.void(),
  responseSchema: ResourceAttributePageSchema,
});

export const CreateResourceAttributeEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/resource-attributes`,
  requestSchema: CreateResourceAttributeRequestSchema,
  responseSchema: ResourceAttributeSchema,
});

export const UpdateResourceAttributeEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ resourceAttributeId }: { brandId?: string; resourceAttributeId: string }) =>
    `v1/resource-attributes/${resourceAttributeId}`,
  requestSchema: UpdateResourceAttributeRequestSchema,
  responseSchema: ResourceAttributeSchema,
});

// ── Guest resource options ───────────────────────────────────────

/** For each rule that lets the guest choose (bike Size), the free units in one session window. */
export const ResourceOptionSchema = z.object({
  resourcePoolId: z.string().uuid(),
  resourcePoolName: z.string(),
  priceTierNames: z.array(z.string()).nullable(),
  attributes: z.array(
    z.object({
      resourceAttributeId: z.string().uuid(),
      name: z.string(),
      values: z.array(ResourceAttributeValueSchema),
    }),
  ),
  /** Free units grouped by the choosable values they carry; a pick fits a combination whose values include it. */
  combinations: z.array(z.object({ valueIds: z.array(z.string().uuid()), available: z.number().int().nonnegative() })),
});
export type ResourceOption = z.infer<typeof ResourceOptionSchema>;

export const GetResourceOptionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ experienceId, startDateTime, duration }: { experienceId: string; startDateTime: string; duration: number }) =>
    `v2/experiences/${experienceId}/resource-options?startDateTime=${encodeURIComponent(
      startDateTime,
    )}&duration=${duration}`,
  requestSchema: z.void(),
  responseSchema: z.object({ items: z.array(ResourceOptionSchema) }),
});

/** What a guest may know about one resource of a pool-backed group (Type: Electric, Size: Large). */
export const ResourceGroupResourceSchema = z.object({
  experienceId: z.string().uuid(),
  attributes: z.array(z.object({ name: z.string(), label: z.string() })),
});
export type ResourceGroupResource = z.infer<typeof ResourceGroupResourceSchema>;

/** Staff view of a group's resources: every attribute, not only the ones a guest may see. */
export const GetStaffResourceGroupResourcesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, resourceGroupId }: { brandId: string; resourceGroupId: string }) =>
    `/v1/brands/${brandId}/resource-groups/${resourceGroupId}/resources`,
  requestSchema: z.void(),
  responseSchema: z.object({ items: z.array(ResourceGroupResourceSchema) }),
});

export const GetResourceGroupResourceOptionsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ resourceGroupId, experienceIds }: { resourceGroupId: string; experienceIds: readonly string[] }) =>
    `v2/resource-groups/${resourceGroupId}/resource-options?experienceIds=${experienceIds.join(',')}`,
  requestSchema: z.void(),
  responseSchema: z.object({ items: z.array(ResourceOptionSchema), resources: z.array(ResourceGroupResourceSchema) }),
});

// ── Pool Units ────────────────────────────────────────────────────

export const ResourcePoolUnitResponseSchema = z.object({
  id: z.string().uuid(),
  resourcePoolId: z.string().uuid(),
  name: z.string(),
  status: resourcePoolUnitStatusSchema,
  /** Free-text notes about the unit, editable at any status ("seat 4F does not recline"). */
  notes: z.string().nullable().optional(),
  /** Upcoming bookings still holding this unit (not yet picked up). */
  upcomingCount: z.number().int().nonnegative().optional(),
  attributeValueIds: z.array(z.string().uuid()),
});

export const ResourcePoolUnitProjectionWireSchema = ResourcePoolUnitResponseSchema.omit({ id: true }).extend({
  resourcePoolUnitId: z.string().uuid(),
});
export type ResourcePoolUnitProjectionWire = z.infer<typeof ResourcePoolUnitProjectionWireSchema>;
export const ResourcePoolUnitProjectionPageSchema = resourcePageSchema(ResourcePoolUnitProjectionWireSchema);

export type ResourcePoolUnitResponse = z.infer<typeof ResourcePoolUnitResponseSchema>;

export const CreatePoolUnitRequestSchema = z.object({
  name: z.string().min(1).max(255),
  attributeValueIds: unitAttributeValueIds().optional(),
});

export type CreatePoolUnitRequest = z.infer<typeof CreatePoolUnitRequestSchema>;

export const UpdatePoolUnitRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  /**
   * Take a unit out of service indefinitely (repair, maintenance) or return
   * it. Distinct from blackout dates and from archiving.
   */
  status: z.enum(['active', 'inactive']).optional(),
  notes: z.string().max(255).nullable().optional(),
  /** Replaces the unit's values. */
  attributeValueIds: unitAttributeValueIds().optional(),
});

export type UpdatePoolUnitRequest = z.infer<typeof UpdatePoolUnitRequestSchema>;

export const ListPoolUnitsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ resourcePoolId }: { brandId?: string; resourcePoolId: string }) =>
    `v1/dashboard/resources/pools/${resourcePoolId}/units`,
  requestSchema: z.void(),
  responseSchema: ResourcePoolUnitProjectionPageSchema,
});

export const CreatePoolUnitEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ resourcePoolId }: { brandId?: string; resourcePoolId: string }) => `v1/resource-pools/${resourcePoolId}/units`,
  requestSchema: CreatePoolUnitRequestSchema,
  responseSchema: ResourcePoolUnitSchema,
});

export const UpdatePoolUnitEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ resourcePoolId, resourcePoolUnitId }: { brandId?: string; resourcePoolId: string; resourcePoolUnitId: string }) =>
    `v1/resource-pools/${resourcePoolId}/units/${resourcePoolUnitId}`,
  requestSchema: UpdatePoolUnitRequestSchema,
  responseSchema: ResourcePoolUnitSchema,
});

export const ArchivePoolUnitEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ resourcePoolId, resourcePoolUnitId }: { brandId?: string; resourcePoolId: string; resourcePoolUnitId: string }) =>
    `v1/resource-pools/${resourcePoolId}/units/${resourcePoolUnitId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

// ── Unit blackout dates ───────────────────────────────────────────────

export const CreateResourcePoolUnitBlackoutRequestSchema = z
  .object({
    startDate: dateString(),
    endDate: dateString().nullable().optional(),
    reason: z.string().max(255).nullable().optional(),
  })
  .strict()
  .refine((data) => !data.endDate || data.endDate >= data.startDate, {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  });
export type CreateResourcePoolUnitBlackoutRequest = z.infer<typeof CreateResourcePoolUnitBlackoutRequestSchema>;

export const ListUnitBlackoutsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ resourcePoolId, resourcePoolUnitId }: { brandId?: string; resourcePoolId: string; resourcePoolUnitId: string }) =>
    `v1/resource-pools/${resourcePoolId}/units/${resourcePoolUnitId}/blackouts`,
  requestSchema: z.void(),
  responseSchema: ResourcePoolUnitBlackoutPageSchema,
});

export const CreateUnitBlackoutEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ resourcePoolId, resourcePoolUnitId }: { brandId?: string; resourcePoolId: string; resourcePoolUnitId: string }) =>
    `v1/resource-pools/${resourcePoolId}/units/${resourcePoolUnitId}/blackouts`,
  requestSchema: CreateResourcePoolUnitBlackoutRequestSchema,
  responseSchema: ResourcePoolUnitBlackoutSchema,
});

export const DeleteUnitBlackoutEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({
    resourcePoolId,
    resourcePoolUnitId,
    startDate,
  }: {
    brandId?: string;
    resourcePoolId: string;
    resourcePoolUnitId: string;
    startDate: string;
  }) => `v1/resource-pools/${resourcePoolId}/units/${resourcePoolUnitId}/blackouts/${startDate}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

// ── At-risk allocations and reallocation ────────────────────────────

/** Query-string inputs shared by the allocation list, the dashboard list, and the reallocate action. */
export type ResourceAllocationQueryParams = {
  readonly brandId?: string;
  readonly experienceId?: string;
  readonly resourcePoolId?: string;
  readonly resourcePoolUnitId?: string;
  readonly bookingId?: string;
  readonly eventId?: string;
  readonly resourceAllocationId?: string | readonly string[];
  readonly status?: ResourceAllocationStatusFilter | readonly ResourceAllocationStatusFilter[];
  readonly simulateDisabledResourcePoolUnitId?: string;
  readonly from?: string;
  readonly to?: string;
  readonly limit?: number;
  readonly after?: string;
  readonly dryRun?: boolean;
};

const toQueryString = (params: ResourceAllocationQueryParams): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === false) continue;
    for (const item of Array.isArray(value) ? value : [value]) search.append(key, String(item));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const ReallocateResourceAllocationsQuerySchema = allocationFilterSchema
  .extend({
    resourceAllocationId: z.union([z.string().uuid(), z.array(z.string().uuid()).max(500)]).optional(),
    /** Present (bare or "true") plans the moves without writing. */
    dryRun: z.enum(['', 'true']).optional(),
  })
  .strict();
export type ReallocateResourceAllocationsQuery = z.infer<typeof ReallocateResourceAllocationsQuerySchema>;

export const ReallocateResourceAllocationResultSchema = z.object({
  reallocationStatus: z.enum(['succeeded', 'failed']),
  reallocationFailReason: z.string().nullable(),
  /** The row after the action; under dryRun the proposed row. */
  resourceAllocation: ResourceAllocationSchema,
});
export type ReallocateResourceAllocationResult = z.infer<typeof ReallocateResourceAllocationResultSchema>;
export const ReallocateResourceAllocationsResponseSchema = z.array(ReallocateResourceAllocationResultSchema);

export const ReallocateResourceAllocationsEndpoint = makeEndpoint({
  method: 'POST',
  url: (params: ResourceAllocationQueryParams) => `v1/resource-allocations:reallocate${toQueryString(params)}`,
  requestSchema: z.object({}).strict(),
  responseSchema: ReallocateResourceAllocationsResponseSchema,
});

/** Core allocation row plus the labels the offline-unit flow shows. */
export const ResourceAllocationProjectionWireSchema = ResourceAllocationSchema.extend({
  experienceName: z.string().nullable(),
  timezoneId: z.string().nullable(),
  purchaserName: z.string().nullable(),
  participantsCount: z.number().int().nullable(),
  confirmationCode: z.string().nullable(),
});
export type ResourceAllocationProjectionWire = z.infer<typeof ResourceAllocationProjectionWireSchema>;
export const ResourceAllocationProjectionPageSchema = resourcePageSchema(ResourceAllocationProjectionWireSchema);

export const ListDashboardResourceAllocationsEndpoint = makeEndpoint({
  method: 'GET',
  url: (params: ResourceAllocationQueryParams) => `v1/dashboard/resources/allocations${toQueryString(params)}`,
  requestSchema: z.void(),
  responseSchema: ResourceAllocationProjectionPageSchema,
});

// ── Allocations ─────────────────────────────────────────────────────

export const UpdateResourceAllocationItemSchema = z
  .object({
    resourceAllocationId: z.string().uuid(),
    status: ResourceAllocationStatusWireSchema.optional(),
    /** Move the allocation to another unit in the same pool. */
    resourcePoolUnitId: z.string().uuid().optional(),
  })
  .strict()
  .refine((item) => Object.keys(item).length > 1, 'Provide at least one field to update');
export type UpdateResourceAllocationItem = z.infer<typeof UpdateResourceAllocationItemSchema>;

export const UpdateResourceAllocationsRequestSchema = z.object({
  items: z.array(UpdateResourceAllocationItemSchema).min(1).max(500),
});
export type UpdateResourceAllocationsRequest = z.infer<typeof UpdateResourceAllocationsRequestSchema>;
export const UpdateResourceAllocationsResponseSchema = z.object({ items: z.array(ResourceAllocationSchema) });

export const ListResourceAllocationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/resource-allocations?brandId=${encodeURIComponent(brandId)}`,
  requestSchema: z.void(),
  responseSchema: ResourceAllocationPageSchema,
});

export const GetResourceAllocationEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ resourceAllocationId }: { brandId?: string; resourceAllocationId: string }) =>
    `v1/resource-allocations/${resourceAllocationId}`,
  requestSchema: z.void(),
  responseSchema: ResourceAllocationSchema,
});

export const UpdateResourceAllocationsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: () => `v1/resource-allocations`,
  requestSchema: UpdateResourceAllocationsRequestSchema,
  responseSchema: UpdateResourceAllocationsResponseSchema,
});

export const DeleteResourceAllocationEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ resourceAllocationId }: { brandId?: string; resourceAllocationId: string }) =>
    `v1/resource-allocations/${resourceAllocationId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

// ── Pool Timeline ───────────────────────────────────────────────────

export const ResourceAllocationBlockSchema = z.object({
  experienceId: z.string(),
  experienceName: z.string(),
  resourcePoolUnitId: z.string().nullable(),
  /** Allocation rows rendered as this block, addressable through the allocation PATCH. */
  resourceAllocationIds: z.array(z.string()),
  /** Null for non-booking allocations (session staffing, holds). */
  bookingId: z.string().nullable(),
  /** BOOKING when absent (rows predating the source model). */
  sourceType: z.nativeEnum(ResourceAllocationSourceType).optional(),
  /** The owning session for SESSION rows (also set for booking rows via their booking). */
  eventId: z.string().nullable().optional(),
  /** SESSION rows only: the departure's confirmed roster, for display. */
  sessionBookings: z
    .array(
      z.object({
        bookingId: z.string(),
        confirmationCode: z.string().nullable(),
        purchaserName: z.string().nullable(),
        participantsCount: z.number(),
      }),
    )
    .optional(),
  confirmationCode: z.string().nullable(),
  purchaserName: z.string().nullable(),
  participantsCount: z.number(),
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
  /** IANA zone of the experience's location — where the guest experiences startAt/endAt. */
  timezoneId: z.string().nullable().optional(),
  allocatedQuantity: z.number(),
  /** What the booking asked for, so a move can warn when the target unit doesn't match. */
  attributeValueIds: z.array(z.string()).optional(),
  /** Pinned "any of" groups: the unit needs at least one value of each. */
  anyOfValueIds: z.array(z.array(z.string())).optional(),
  /** The guest chose this exact unit; the timeline refuses to move it. */
  requested: z.boolean().optional(),
  /** Booked through a pool-backed resource group: the group and collection that sold the unit. */
  resourceGroupName: z.string().nullable().optional(),
  collectionName: z.string().nullable().optional(),
  /** HELD only: when the pending payment's hold lapses. */
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
  status: ResourceAllocationStatusWireSchema,
  /** When staff marked the resource as physically handed out. */
  pickedUpAt: z.string().datetime({ offset: true }).nullable().optional(),
  /** When staff marked the resource as physically returned. */
  returnedAt: z.string().datetime({ offset: true }).nullable().optional(),
  /** When staff marked the booking a no-show — resources released, booking untouched. */
  noShowAt: z.string().datetime({ offset: true }).nullable().optional(),
});

export type ResourceAllocationBlock = z.infer<typeof ResourceAllocationBlockSchema>;

export const TimelineUnitBlackoutSchema = z.object({
  startDate: dateString(),
  endDate: dateString().nullable(),
  reason: z.string().nullable(),
});

export const TimelineUnitSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: resourcePoolUnitStatusSchema,
  notes: z.string().nullable(),
  /** False when the unit is not active or a blackout covers the timeline's first day. */
  isAvailable: z.boolean(),
  /** The blackout covering the timeline's first day, when there is one. */
  blackout: TimelineUnitBlackoutSchema.nullable(),
  attributeValueIds: z.array(z.string()).optional(),
});

export type TimelineUnit = z.infer<typeof TimelineUnitSchema>;

export const PoolTimelineEntrySchema = z.object({
  poolId: z.string(),
  poolName: z.string(),
  /** Pool-level status; an inactive pool sells nothing regardless of its units' own status. */
  status: z.nativeEnum(ResourcePoolStatus).optional(),
  type: z.nativeEnum(ResourcePoolType).optional(),
  unitTrackingMode: unitTrackingModeSchema.optional(),
  unitQuantity: z.number(),
  /** Seats per unit; null when not applicable. */
  capacityPerUnit: z.number().nullable().optional(),
  effectiveQuantity: z.number(),
  hasUnits: z.boolean(),
  attributes: z.array(ResourcePoolAttributeSchema).optional(),
  units: z.array(TimelineUnitSchema),
  allocations: z.array(ResourceAllocationBlockSchema),
});

export type PoolTimelineEntry = z.infer<typeof PoolTimelineEntrySchema>;

export const ResourceAllocationBlockWireSchema = ResourceAllocationBlockSchema;
export const TimelineUnitWireSchema = TimelineUnitSchema.omit({ id: true }).extend({
  resourcePoolUnitId: z.string(),
});
export const PoolTimelineEntryWireSchema = PoolTimelineEntrySchema.omit({
  poolId: true,
  units: true,
  allocations: true,
}).extend({
  resourcePoolId: z.string(),
  units: z.array(TimelineUnitWireSchema),
  allocations: z.array(ResourceAllocationBlockWireSchema),
});
export type PoolTimelineEntryWire = z.infer<typeof PoolTimelineEntryWireSchema>;

export const GetPoolTimelineEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, from, to }: { brandId: string; from: string; to: string }) =>
    `v1/dashboard/resources/timeline?brandId=${encodeURIComponent(brandId)}&from=${encodeURIComponent(
      from,
    )}&to=${encodeURIComponent(to)}`,
  requestSchema: z.void(),
  responseSchema: z.object({ items: z.array(PoolTimelineEntryWireSchema) }),
});

/** One atomic lifecycle action across selected pools for one booking/window. */
export const BookingResourceTransitionSchema = z
  .object({
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    action: z.enum(['PICK_UP', 'RETURN', 'NO_SHOW']),
    undo: z.boolean().default(false),
    selections: z
      .array(
        z
          .object({
            resourcePoolId: z.string().uuid(),
            resourcePoolUnitIds: z.array(z.string().uuid()).min(1).max(500).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict();
export type BookingResourceSelection = z.infer<typeof BookingResourceTransitionSchema>['selections'][number];
export const TransitionBookingResourcesEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ bookingId }: { bookingId: string }) => `v1/dashboard/resources/bookings/${bookingId}:transition`,
  requestSchema: BookingResourceTransitionSchema,
  responseSchema: z.void(),
});
